/**
 * Everyone else in the system.
 *
 * The room hands out ship states as they change; the render loop needs to know
 * where each one is *this frame*, sixty times a second. Those are different
 * shapes, and this is the thing between them.
 *
 * ── Why a mutable map rather than React state ───────────────────────────────
 *
 * Because nothing here belongs on screen as text. Peers arrive ten times a
 * second and are read by a loop that already runs off refs; routing them through
 * React would re-render the entire HUD on every remote ship's every update, to
 * change nothing a player can read. The map is mutated in place and the loop
 * reads it — the same arrangement the flight loop already has with its own hull.
 *
 * ── Interpolation ───────────────────────────────────────────────────────────
 *
 * Ten updates a second against sixty frames drawn. Something has to fill the
 * gaps or every remote ship moves in visible steps.
 *
 * Each peer keeps the last two poses it sent and the loop draws between them,
 * deliberately a beat behind — see `INTERPOLATION_DELAY`. Extrapolating from
 * velocity instead would be a frame ahead rather than a beat behind, at the cost
 * of being *wrong* whenever somebody turns, and a ship that overshoots and snaps
 * back reads far worse than one drawn a tenth of a second late.
 */

import * as THREE from 'three';
import type { Room } from 'colyseus.js';
import { getStateCallbacks } from 'colyseus.js';
import type { ShipGraph, ShipState, SystemState } from '@interspacial/shared';

/**
 * How far behind live a remote ship is drawn, in seconds.
 *
 * One publish interval. Drawing at `now` would mean the newest pose has not
 * arrived yet for most of every beat, leaving nothing to interpolate *towards* —
 * so the ship would reach its last known position and stop dead until the next
 * one landed, sixty times a second forever. A beat of delay means there are
 * always two poses bracketing the moment being drawn.
 *
 * A tenth of a second of lag on somebody else's ship is not perceptible. A ship
 * that stutters is.
 */
export const INTERPOLATION_DELAY = 0.1;

/**
 * The clock both ends of this must agree on.
 *
 * Timestamps are written when a pose arrives — off the room's callbacks, in
 * React's world — and read when a frame is drawn, inside a scene that is torn
 * down and rebuilt on every jump. A clock belonging to either one would restart
 * under the other. `performance.now` belongs to the page, is monotonic, and is
 * unaffected by the wall clock moving.
 */
export function peerClock(): number {
  return performance.now() / 1000;
}

export interface PeerPose {
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  /**
   * What this sample is measured from, or empty for absolute.
   *
   * Per sample rather than per peer, because a ship entering or leaving a mass
   * lock changes frames mid-flight -- and two samples in different frames cannot
   * be interpolated between, whatever the numbers say.
   */
  anchor: string;
  /** When this arrived, in seconds on the same clock the render loop uses. */
  at: number;
}

export interface Peer {
  /** The room's session id, which is also the ship's id. */
  id: string;
  ownerUuid: string;
  /** The ship's callsign — its owner's short display id. */
  callsign: string;
  /** In the warp tunnel: between systems, and not to be drawn. */
  hidden: boolean;
  /**
   * Parked on a pad.
   *
   * Drawn from the berth rather than from `previous`/`latest`, which carry
   * nothing meaningful while this is true -- see `stationId`. Also what makes a
   * pad count as taken.
   */
  docked: boolean;
  stationId: string;
  padId: string;
  /**
   * What the ship looks like, or null until its owner has said.
   *
   * Rebuilt from the wire rather than shared with the sender, so a peer's hull
   * is ordinary data here and cannot be confused for our own.
   */
  graph: ShipGraph | null;
  /**
   * Bumped whenever `graph` is replaced.
   *
   * The renderer holds a built mesh per peer and needs to know when to throw it
   * away. Comparing graphs would mean walking every line each frame; a counter
   * is one integer compare.
   */
  revision: number;
  /** The two most recent poses, oldest first. Equal until a second arrives. */
  previous: PeerPose;
  latest: PeerPose;
}

export interface PeerFeed {
  /** Keyed by session id. Mutated in place; never replaced. */
  peers: Map<string, Peer>;
}

function poseOf(ship: ShipState, at: number): PeerPose {
  return {
    x: ship.x,
    y: ship.y,
    z: ship.z,
    qx: ship.qx,
    qy: ship.qy,
    qz: ship.qz,
    qw: ship.qw,
    anchor: ship.anchor,
    at,
  };
}

/**
 * Watch a room's ships, and keep a feed the render loop can read.
 *
 * Our own ship is skipped: we already know where it is, to sixty decimal places
 * and sixty times a second, and drawing the room's opinion of it would show us a
 * copy of ourselves a beat in the past.
 */
export function watchPeers(room: Room<SystemState>, clock: () => number): PeerFeed {
  const feed: PeerFeed = { peers: new Map() };
  const $ = getStateCallbacks(room);

  $(room.state).ships.onAdd((ship, key: string) => {
    if (key === room.sessionId) return;

    const now = clock();
    const peer: Peer = {
      id: key,
      ownerUuid: ship.ownerUuid,
      // Until a pose arrives there is no reason to believe a position, so a new
      // peer starts unrendered rather than at the origin — which, with the
      // floating origin, is exactly where the player is sitting.
      callsign: ship.callsign,
      hidden: true,
      docked: false,
      stationId: '',
      padId: '',
      graph: null,
      revision: 0,
      previous: poseOf(ship, now),
      latest: poseOf(ship, now),
    };
    feed.peers.set(key, peer);

    /*
     * One listener for the whole ship rather than one per field.
     *
     * Colyseus fires this once per patch, after everything in it has been
     * applied, so a pose that moved and turned lands as a single sample. Per
     * field it would land as seven, six of them a mix of the new pose and the
     * old.
     */
    $(ship).onChange(() => {
      const at = clock();

      // A pose that did not move the ship is not a new sample. Rolling the
      // buffer on it would collapse the interpolation window to nothing and
      // make the ship jump — a graph update alone must not disturb motion.
      // A parked ship is not moving, and the pose it last sent while flying is
      // not where it is. Rolling the buffer on a docked update would interpolate
      // it away from a pad it is sitting still on.
      const moved =
        !ship.docked &&
        (ship.anchor !== peer.latest.anchor ||
          ship.x !== peer.latest.x ||
        ship.y !== peer.latest.y ||
        ship.z !== peer.latest.z ||
        ship.qx !== peer.latest.qx ||
        ship.qy !== peer.latest.qy ||
        ship.qz !== peer.latest.qz ||
        ship.qw !== peer.latest.qw);

      if (moved) {
        peer.previous = peer.latest;
        peer.latest = poseOf(ship, at);
      }

      peer.hidden = ship.hidden;
      peer.ownerUuid = ship.ownerUuid;
      peer.callsign = ship.callsign;
      peer.docked = ship.docked;
      peer.stationId = ship.stationId;
      peer.padId = ship.padId;
    });

    /*
     * The graph, rebuilt when it changes.
     *
     * `onChange` on the ship does not fire for its child arrays, so appearance
     * gets its own listeners. It changes on join and when a wall is lost, so the
     * cost of rebuilding the whole graph rather than tracking which line moved
     * is a few dozen objects, rarely.
     *
     * ── Why the microtask ────────────────────────────────────────────────────
     *
     * These fire once per *element*, not once per patch. A starter ship joining
     * fires twenty-two times — seven joints, eight lines, seven components — and
     * a large hull fires hundreds. Each one bumps the revision, and the renderer
     * throws away and rebuilds the entire mesh on a revision change, so a single
     * ship arriving meant a hundred full mesh builds in one frame.
     *
     * Colyseus applies a patch synchronously, so a microtask runs once after the
     * whole thing has landed. One rebuild, from the finished graph.
     *
     * This is the same shape as the collision freeze: work that looked like it
     * happened once was happening per element, and the symptom was a stall
     * rather than a wrong answer.
     */
    let queued = false;

    const rebuild = () => {
      peer.graph = {
        joints: [...ship.joints].map((joint) => ({ id: joint.id, x: joint.x, y: joint.y })),
        lines: [...ship.lines].map((line) => ({
          id: line.id,
          a: line.a,
          b: line.b,
          health: line.health,
          strength: line.strength,
          length: line.length,
          color: line.color,
        })),
        // A component whose type did not survive the trip is dropped rather
        // than guessed at. Guessing puts a part on somebody's ship that they do
        // not have, and every guess is wrong in a different way.
        components: [...ship.components]
          .filter((part) => part.componentType !== '')
          .map((part) => ({
            id: part.id,
            type: part.componentType as Exclude<typeof part.componentType, ''>,
            position: { x: part.x, y: part.y },
          })),
      };
      peer.revision += 1;
    };

    const rebuildOnce = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        // The peer may have left between the patch and the microtask, in which
        // case there is nothing to rebuild for.
        if (feed.peers.get(key) === peer) rebuild();
      });
    };

    $(ship).joints.onAdd(rebuildOnce);
    $(ship).joints.onRemove(rebuildOnce);
    $(ship).lines.onAdd(rebuildOnce);
    $(ship).lines.onRemove(rebuildOnce);
    $(ship).components.onAdd(rebuildOnce);
    $(ship).components.onRemove(rebuildOnce);
  });

  $(room.state).ships.onRemove((_ship, key: string) => {
    feed.peers.delete(key);
  });

  return feed;
}

/**
 * Where a peer is right now, written into `out`.
 *
 * `now` is on the same clock `watchPeers` was given. Writes into a caller's
 * objects rather than returning new ones: this runs once per peer per frame, and
 * allocating two vectors and a quaternion sixty times a second per ship is the
 * kind of garbage that shows up as stutter rather than as slowness.
 *
 * Clamped at both ends. Before the window it is the older pose; after it — which
 * means the sender has gone quiet, from lag or a browser tab in the background —
 * the ship holds at the newest pose known rather than sailing off along its last
 * heading.
 */
export function interpolate(peer: Peer, now: number, out: THREE.Object3D): void {
  const at = now - INTERPOLATION_DELAY;
  const span = peer.latest.at - peer.previous.at;

  /*
   * Two samples in different frames are not two points on one path.
   *
   * A ship crossing into a mass lock starts measuring from the body instead of
   * from nothing, so the numbers jump by the body's whole distance from the
   * origin. Interpolating across that would fling the ship through the system
   * and back. The newest sample is simply used as it stands.
   */
  const sameFrame = peer.previous.anchor === peer.latest.anchor;
  const t =
    sameFrame && span > 0 ? Math.min(1, Math.max(0, (at - peer.previous.at) / span)) : 1;

  const a = peer.previous;
  const b = peer.latest;

  out.position.set(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);

  /*
   * Nearest-arc before lerping.
   *
   * q and −q are the same orientation, so two consecutive poses can be numerically
   * opposite while describing almost the same attitude — and a straight lerp
   * between them takes the long way round, spinning the ship most of a turn
   * between one frame and the next. Flipping one first is the whole fix.
   *
   * Lerped rather than slerped: at a tenth of a second apart the two are within
   * a fraction of a degree of each other, and normalising afterwards is enough.
   */
  const dot = a.qx * b.qx + a.qy * b.qy + a.qz * b.qz + a.qw * b.qw;
  const sign = dot < 0 ? -1 : 1;

  out.quaternion.set(
    a.qx + (b.qx * sign - a.qx) * t,
    a.qy + (b.qy * sign - a.qy) * t,
    a.qz + (b.qz * sign - a.qz) * t,
    a.qw + (b.qw * sign - a.qw) * t,
  );
  out.quaternion.normalize();
}

/**
 * A line somebody said, as the room broadcasts it.
 *
 * `from` and `uuid` come from the verified token on the server, never from the
 * message — a client that could choose its own name could put words in
 * anybody's mouth.
 */
export interface ChatLine {
  /** The pilot's name — their chaostree username. */
  from: string;
  /** Their ship's callsign — the short display id. */
  callsign: string;
  uuid: string;
  text: string;
  channel: string;
  /** Spent a beacon: shown as an interrupt rather than a line in the log. */
  alert: boolean;
  at: number;
}
